import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  MenuItem,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import { DialogTitleWithClose } from '@/components/common/DialogTitleWithClose';
import { ConfigContext } from '@/Contexts/ConfigContext';
import useUser from '@/hooks/useUser';
import { CountryProps } from '@/models/Country';
import { StateProps } from '@/models/State';
import { UserProps } from '@/models/User';
import { USCountryId } from '@/utils/constants';
import { SelectedPartnerContext } from '@/Contexts/SelectedPartnerContext';
import { PartnerGroupSelectModel } from '@/models/PartnerGroupSelectModel';
import { stringHasValue } from '@/utils/stringUtil';
import { logError } from '@/utils/errorHandlerUtil';

export default function ApplicantModal({
  applicantModalOpen,
  modalUser: user,
  theme,
  handleRowUpdate,
  setApplicantModalOpen,
  setApplicantModalClosing,
}: {
  applicantModalOpen: boolean;
  modalUser: UserProps;
  theme: Theme;
  handleRowUpdate: (users: UserProps[]) => void;
  setApplicantModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setApplicantModalClosing: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const { postUser, updateUsers, resetUserToIntake } = useUser();
  const { setAlert } = useContext(SnackbarContext);
  const { partnerGroupSelectOptions } = useContext(SelectedPartnerContext);
  interface PartnerGroupSelectOption {
    id: string;
    partnerName: string;
  }

  const [selectedPartnerGroup, setSelectedPartnerGroup] = useState<PartnerGroupSelectOption | null>(
    null
  );
  const [partnerGroupOptions, setPartnerGroupOptions] = useState([]);

  const [postingUser, setPostingUser] = useState<boolean>(false);

  const { allStates, allCountries } = useContext(ConfigContext);

  const SEND_TO_INTAKE_LABEL = 'Reset to Intake';

  const SendUserToIntake = async (): Promise<UserProps> => {
    if (!stringHasValue(user?.b2CId)) throw new Error('User B2C ID is null or empty');
    return await resetUserToIntake(user.b2CId);
  };

  const updateUserPartnerGroup = async (updatedUserPartnerGroup: PartnerGroupSelectOption) => {
    if (isNaN(Number(updatedUserPartnerGroup.id))) {
      throw new Error(
        'Could not update partner group id, selected option is not a number. Selected ID: ' +
          updatedUserPartnerGroup.id
      );
    }

    const userInfo: UserProps = {
      ...user,
      partnerGroupId: Number(updatedUserPartnerGroup.id),
      contactId: user.contactId,
    } satisfies UserProps;

    return await postUser(userInfo);
  };

  const handlePartnerGroupOptionSelect = async () => {
    if (selectedPartnerGroup == null) {
      throw new Error('No partner group selected');
    }

    setPostingUser(true);

    try {
      const isSendToIntake = selectedPartnerGroup.partnerName === SEND_TO_INTAKE_LABEL;
      let userData: UserProps | null = null;

      if (isSendToIntake) userData = await SendUserToIntake();
      else userData = await updateUserPartnerGroup(selectedPartnerGroup);
      setApplicantModalOpen(false);
      setApplicantModalClosing(true);

      if (selectedPartnerGroup != null && user.partnerGroup != null) {
        user.partnerGroup.id = Number(selectedPartnerGroup.id);
        user.partnerGroup.partnerName = isSendToIntake ? null : selectedPartnerGroup.partnerName;
        handleRowUpdate([user]);
      }

      updateUsers(userData);

      setAlert({
        severity: 'success',
        message: `Successfully updated user partner group for user with B2C ID: ${user?.b2CId}.`,
      });
    } catch (error) {
      logError(`Error updating user partner group for user with B2C ID: ${user?.b2CId}.`, error);
      setAlert({
        severity: 'error',
        message: `Error updating user partner group for user with B2C ID: ${user?.b2CId}.`,
      });
    }

    setPostingUser(false);
  };

  useEffect(() => {
    if (
      selectedPartnerGroup == null &&
      user != null &&
      user.partnerGroupId != null &&
      user.partnerGroup?.partnerName != null
    ) {
      const defaultPartnerGroup: PartnerGroupSelectOption = {
        id: user.partnerGroupId.toString(),
        partnerName: user.partnerGroup.partnerName,
      };

      setSelectedPartnerGroup(defaultPartnerGroup);
    }
  }, [user, selectedPartnerGroup]);

  useEffect(() => {
    if (
      partnerGroupSelectOptions?.length > 0 &&
      allCountries?.length > 0 &&
      allStates?.length > 0
    ) {
      const options: any = partnerGroupSelectOptions
        .map((partnerGroup: PartnerGroupSelectModel) => {
          if (partnerGroup.partnerGroupId != null) {
            let label: string | undefined = partnerGroup.partnerGroupName;

            const state = allStates.find((s: StateProps) => s.id == partnerGroup.stateId);
            const country = allCountries.find((c: CountryProps) => c.id == partnerGroup.countryId);
            const stateName =
              country != null && partnerGroup.countryId == USCountryId && state != null
                ? ` (${state.name})`
                : null;
            const countryName =
              country != null && partnerGroup.countryId != USCountryId
                ? ` (${country.name})`
                : null;
            const other = stateName != null ? stateName : countryName != null ? countryName : '';
            label = `${label}${other}`;

            return {
              id: partnerGroup.partnerGroupId,
              partnerName: label,
            };
          }
        })
        .sort((a: any, b: any) => a.partnerName.localeCompare(b.partnerName));

      // add a blank option at the start of the list with a value of null and label of Reset to Intake
      options.unshift({ id: null, partnerName: SEND_TO_INTAKE_LABEL });

      setPartnerGroupOptions(options);
    }
  }, [partnerGroupSelectOptions, allCountries, allStates, SEND_TO_INTAKE_LABEL]);

  const handleClose = () => {
    setApplicantModalOpen(false);
  };

  return (
    <Dialog open={applicantModalOpen} onClose={handleClose}>
      <DialogTitleWithClose onClose={handleClose}>Re-Assign User</DialogTitleWithClose>
      <DialogContent>
        <Box>
          <Typography variant='overline2'>Applicant</Typography>
          <Typography color='textSecondary'>
            {`${user?.contact?.firstName} ${user?.contact?.lastName} (${user?.email})`}
          </Typography>
        </Box>

        <Autocomplete
          size='small'
          options={partnerGroupOptions != null ? partnerGroupOptions : []}
          value={partnerGroupOptions != null && selectedPartnerGroup ? selectedPartnerGroup : ''}
          getOptionLabel={(option) => option.partnerName ?? ''}
          onChange={(event: any, partnerGroupOption: any) => {
            setSelectedPartnerGroup(partnerGroupOption);
          }}
          renderOption={(props, option) => (
            <MenuItem {...props} key={option.id} value={option.id}>
              {option.partnerName}
            </MenuItem>
          )}
          renderInput={(params) => <TextField {...params} label='Partner Group' />}
        />
      </DialogContent>
      <DialogActions>
        <Button variant='secondary' size='large' onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant='primary'
          size='large'
          onClick={handlePartnerGroupOptionSelect}
          disabled={postingUser}
        >
          {postingUser ? (
            <>
              <span>Updating ... </span>
              <CircularProgress
                style={{ color: theme.palette.grey[500], marginLeft: '5px' }}
                size='16px'
              />
            </>
          ) : (
            'Update Partner Group'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
